import NextLink from 'next/link';
import {
  Box,
  HStack,
  Link,
  SimpleGrid,
  Spacer,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';

type Department = {
  id: number;
  name: string;
  articlesCount: number;
};

function DepartmentCard({
  department,
  hasBoxShadow = true,
}: {
  department: Department;
  hasBoxShadow?: boolean;
}) {
  const nameColor = department.articlesCount > 0 ? undefined : 'gray';
  const iconColor = department.articlesCount > 0 ? 'brand.500' : 'gray';
  const countString =
    department.articlesCount > 0
      ? String(department.articlesCount)
      : 'スライド募集中';

  return (
    <Box
      boxShadow={hasBoxShadow ? '0 0 8px rgba(0,0,0,0.08)' : undefined}
      borderRadius="lg"
      overflow="hidden"
    >
      <HStack px={[0, null, 4]} py={[2, null, 4]}>
        <Text fontSize="sm" fontWeight="bold" color={nameColor}>
          {department.name}
          <Text
            as="span"
            fontSize="xs"
            fontWeight="normal"
            color="gray"
            ms={1}
            display={'inline-block'}
          >
            （{countString}）
          </Text>
        </Text>
        <Spacer />
        <ChevronRightIcon color={iconColor} w={6} h={6} />
      </HStack>
    </Box>
  );
}

function LinkifiedDepartmentCard({
  department,
  hasBoxShadow = true,
  gaEventLabel,
}: {
  department: Department;
  hasBoxShadow?: boolean;
  gaEventLabel?: string;
}) {
  if (department.articlesCount === 0) {
    return (
      <DepartmentCard department={department} hasBoxShadow={hasBoxShadow} />
    );
  }

  return (
    <NextLink href={`/department/${department.id}`} passHref>
      <Link
        display="inline-block"
        color="inherit"
        _hover={{
          textDecoration: 'none',
        }}
        onClick={() =>
          gaEventLabel &&
          sendAnalyticsEvent('department', 'click', gaEventLabel)
        }
      >
        <DepartmentCard department={department} hasBoxShadow={hasBoxShadow} />
      </Link>
    </NextLink>
  );
}

/**
 * 診療科一覧表示
 */
export default function DepartmentsGrid({
  departments,
  gaEventLabel,
}: {
  departments: Department[];
  gaEventLabel?: string;
}) {
  const hasBoxShadow = useBreakpointValue({
    base: false,
    md: true,
  });
  return (
    <SimpleGrid columns={[2, null, 3]} spacing={4} w="full">
      {departments.map((department) => (
        <LinkifiedDepartmentCard
          key={department.id}
          department={department}
          hasBoxShadow={hasBoxShadow}
          gaEventLabel={gaEventLabel}
        />
      ))}
    </SimpleGrid>
  );
}
